export const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

export const WEEKDAY_OPTIONS = [
  { value: 1, text: '週一' },
  { value: 2, text: '週二' },
  { value: 3, text: '週三' },
  { value: 4, text: '週四' },
  { value: 5, text: '週五' },
  { value: 6, text: '週六' },
  { value: 7, text: '週日' }
]

export const MONTH_WEEK_OPTIONS = [1, 2, 3, 4, 5, 6]

const TYPE_BADGES = {
  day: '每天固定',
  week: '每週固定',
  cycle: '週期性',
  date: '特定日'
}

const pad = value => String(value).padStart(2, '0')

export const START_TIME_OPTIONS = Array.from({ length: 24 * 12 }, (_, index) => {
  const minutes = index * 5
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
})

export const END_TIME_OPTIONS = Array.from({ length: 24 * 12 + 1 }, (_, index) => {
  const minutes = index * 5
  return minutes === 24 * 60
    ? '24:00'
    : `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
})

export function cloneOffworkValue(value) {
  return JSON.parse(JSON.stringify(value))
}

export function createEmptyOffworkRule(key) {
  return {
    id: null,
    _key: key,
    editor_type: 'week',
    rule_type: 'weekly',
    action_type: 'closed',
    specific_date: '',
    note: '',
    weekdays: [7],
    month_weeks: [],
    periods: [{ start: '09:00', end: '18:00' }],
    is_all_day: false
  }
}

export function apiRuleToEditor(rule, key) {
  const weekdays = sortNumbers(rule.weekdays || [])
  let editorType = 'cycle'

  if (rule.rule_type === 'specific_date') {
    editorType = 'date'
  } else if (rule.rule_type === 'weekly') {
    editorType = sameNumbers(weekdays, ALL_WEEKDAYS) ? 'day' : 'week'
  }

  const periods = cloneOffworkValue(rule.periods || [])
  const isAllDay = periods.length === 1 && periods[0].start === '00:00' && periods[0].end === '24:00'

  return {
    id: rule.id,
    _key: key,
    editor_type: editorType,
    rule_type: rule.rule_type,
    action_type: rule.action_type,
    specific_date: rule.specific_date || '',
    note: rule.note || '',
    weekdays,
    month_weeks: sortNumbers(rule.month_weeks || []),
    periods: isAllDay ? [{ start: '09:00', end: '18:00' }] : periods,
    is_all_day: isAllDay
  }
}

export function editorRuleToPayload(rule) {
  const payload = {
    rule_type: resolveRuleType(rule),
    action_type: rule.editor_type === 'date' ? rule.action_type : 'closed',
    specific_date: rule.editor_type === 'date' ? rule.specific_date : null,
    note: rule.editor_type === 'date' ? normalizeNote(rule.note) : null,
    weekdays: ['week', 'cycle'].includes(rule.editor_type)
      ? sortNumbers(rule.weekdays || [])
      : (rule.editor_type === 'day' ? [...ALL_WEEKDAYS] : []),
    month_weeks: rule.editor_type === 'cycle' && rule.rule_type === 'selected_weeks'
      ? sortNumbers(rule.month_weeks || [])
      : [],
    periods: rule.is_all_day
      ? [{ start: '00:00', end: '24:00' }]
      : sortPeriods(rule.periods || [])
  }

  return payload
}

export function validateOffworkRule(rule) {
  if (rule.editor_type === 'date' && !rule.specific_date) {
    return '請選擇日期'
  }

  if (rule.editor_type === 'date' && [...String(rule.note || '')].length > 255) {
    return '備註不可超過 255 字'
  }

  if (['week', 'cycle'].includes(rule.editor_type) && !(rule.weekdays || []).length) {
    return '請至少選擇一個星期'
  }

  if (rule.editor_type === 'cycle' && rule.rule_type === 'selected_weeks' && !(rule.month_weeks || []).length) {
    return '請至少選擇一個週次'
  }

  if (rule.is_all_day) return ''

  if (!(rule.periods || []).length) {
    return '請至少設定一個時間區段'
  }

  const normalizedPeriods = []
  for (const period of rule.periods) {
    if (!isValidStartTime(period.start) || !isValidEndTime(period.end)) {
      return '請選擇有效的開始與結束時間'
    }
    const start = timeToMinutes(period.start)
    const end = timeToMinutes(period.end)
    if (start === end) {
      return '開始與結束時間不可相同'
    }
    normalizedPeriods.push({
      start,
      end: end + (start > end ? 24 * 60 : 0)
    })
  }

  for (let leftIndex = 0; leftIndex < normalizedPeriods.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < normalizedPeriods.length; rightIndex += 1) {
      if (periodsOverlapOrTouch(normalizedPeriods[leftIndex], normalizedPeriods[rightIndex])) {
        return '同一規則的時間區段不可重疊或相接'
      }
    }
  }

  return ''
}

function periodsOverlapOrTouch(left, right) {
  return [-24 * 60, 0, 24 * 60].some(offset => {
    const shiftedStart = right.start + offset
    const shiftedEnd = right.end + offset
    return Math.max(left.start, shiftedStart) <= Math.min(left.end, shiftedEnd)
  })
}

export function isSameRulePayload(left, right) {
  return JSON.stringify(editorRuleToPayload(left)) === JSON.stringify(editorRuleToPayload(right))
}

export function getRuleBadge(rule) {
  return TYPE_BADGES[rule.editor_type] || TYPE_BADGES.cycle
}

export function getRuleTitle(rule) {
  if (rule.editor_type === 'day') return '每天固定休診'
  if (rule.editor_type === 'date') return formatSpecificDate(rule.specific_date)

  const weekdayText = formatWeekdays(rule.weekdays)
  if (rule.editor_type === 'week') return `每週 ${weekdayText}`

  const cycleText = rule.rule_type === 'odd_weeks'
    ? '每月單數週'
    : (rule.rule_type === 'even_weeks' ? '每月雙數週' : formatMonthWeeks(rule.month_weeks))
  return `${cycleText}・${weekdayText}`
}

export function getRuleActionText(rule) {
  return rule.action_type === 'open' ? '照常營業' : '休診'
}

export function getRulePeriodText(rule) {
  if (rule.is_all_day) return '全天'
  return sortPeriods(rule.periods || []).map(period => {
    const endPrefix = timeToMinutes(period.start) > timeToMinutes(period.end) ? '次日 ' : ''
    return `${period.start}－${endPrefix}${period.end}`
  }).join('、')
}

function normalizeNote(note) {
  const value = String(note || '').trim()
  return value || null
}

function resolveRuleType(rule) {
  if (rule.editor_type === 'date') return 'specific_date'
  if (['day', 'week'].includes(rule.editor_type)) return 'weekly'
  return rule.rule_type
}

function formatSpecificDate(date) {
  if (!date) return '尚未選擇日期'
  const [year, month, day] = date.split('-').map(Number)
  const weekday = WEEKDAY_OPTIONS[(new Date(year, month - 1, day).getDay() + 6) % 7].text
  return `${year} 年 ${month} 月 ${day} 日（${weekday}）`
}

function formatWeekdays(weekdays) {
  if (sameNumbers(sortNumbers(weekdays || []), ALL_WEEKDAYS)) return '每天'
  return sortNumbers(weekdays || []).map(value => {
    const option = WEEKDAY_OPTIONS.find(item => item.value === value)
    return option ? option.text : ''
  }).filter(Boolean).join('、') || '尚未選擇星期'
}

function formatMonthWeeks(monthWeeks) {
  const weeks = sortNumbers(monthWeeks || [])
  return weeks.length ? `每月第 ${weeks.join('、')} 週` : '尚未選擇週次'
}

function isValidStartTime(value) {
  return START_TIME_OPTIONS.includes(value)
}

function isValidEndTime(value) {
  return END_TIME_OPTIONS.includes(value)
}

function timeToMinutes(value) {
  if (value === '24:00') return 24 * 60
  const [hour, minute] = String(value).split(':').map(Number)
  return hour * 60 + minute
}

function sortPeriods(periods) {
  return cloneOffworkValue(periods).sort((left, right) => timeToMinutes(left.start) - timeToMinutes(right.start))
}

function sortNumbers(values) {
  return [...values].map(Number).sort((left, right) => left - right)
}

function sameNumbers(left, right) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}
